import { CLASS_NAMES, EVENTS } from '@bs/constants';
import { MessageBus } from '@bs/utils';

/**
 * Class representing an image scrolling component
 */
export default class ImageScroll extends MessageBus {
  constructor(element) {
    super();
    this.element = element;
    if (!document.querySelector(`.${CLASS_NAMES.IMAGE_SCROLLING_JS}`)) {
      return;
    }

    this.init();
  }

  init() {
    this.cacheDomReferences();
    this.setupIntersectionObserver();
    this.setupEventListeners();
    this.handleResize();

    // Initialize first image
    document
      .querySelector(`#${CLASS_NAMES.INDEX_0}`)
      .classList.add(CLASS_NAMES.ACTIVE);
  }

  cacheDomReferences() {
    this.main = document.querySelector(`.${CLASS_NAMES.IMAGE_SCROLLING_JS}`);
    this.scrolly = this.main.querySelector(
      `.${CLASS_NAMES.IMAGE_SCROLLING_ITEM_JS}`
    );
    this.figure = this.scrolly.querySelector(
      `.${CLASS_NAMES.IMAGE_SCROLLING_COLUMN_LEFT_JS}`
    );
    this.article = this.scrolly.querySelector(
      `.${CLASS_NAMES.IMAGE_SCROLLING_CONTENT_AREA_JS}`
    );
    this.steps = this.article.querySelectorAll(
      `.${CLASS_NAMES.IMAGE_SCROLLING_COLUMN_RIGHT_JS}`
    );
    this.images = document.querySelectorAll(
      `.${CLASS_NAMES.IMAGE_SCROLLING_IMAGE}`
    );
  }

  setupEventListeners() {
    window.addEventListener(EVENTS.RESIZE, () => this.handleResize());
  }

  handleResize() {
    // Update height of step elements
    const stepHeight = Math.floor(window.innerHeight * 0.75);
    this.steps.forEach((step) => {
      const stepItem = step;
      stepItem.style.height = `${stepHeight}px`;
    });

    const figureHeight = window.innerHeight / 2;
    const figureMarginTop = (window.innerHeight - figureHeight) / 2;

    this.figure.style.height = `${figureHeight}px`;
    this.figure.style.top = `${figureMarginTop}px`;
  }

  setupIntersectionObserver() {
    if (window.innerWidth < 1024) return;

    const options = {
      root: null,
      rootMargin: '-50% 0px -50% 0px', // Triggers when element is in center
      threshold: 0
    };

    let lastScrollY = window.scrollY;
    let scrollDirection = 'down';

    // Define scroll handler as a const
    const scrollHandler = () => {
      scrollDirection = window.scrollY > lastScrollY ? 'down' : 'up';
      lastScrollY = window.scrollY;
    };

    // Track scroll direction
    window.addEventListener(EVENTS.SCROLL, scrollHandler);

    const callback = (entries) => {
      entries.forEach((entry) => {
        if (entry.isIntersecting) {
          const currentStep = entry.target;
          const index = Array.from(this.steps).indexOf(currentStep);

          // Remove active class from all steps
          this.steps.forEach((step) =>
            step.classList.remove(CLASS_NAMES.IS_ACTIVE)
          );
          // Add active class to current step
          currentStep.classList.add(CLASS_NAMES.IS_ACTIVE);

          // Update images based on scroll direction
          this.images.forEach((img) =>
            img.classList.remove(CLASS_NAMES.ACTIVE)
          );

          if (scrollDirection === 'down') {
            // When scrolling down, show only current image
            document
              .querySelector(`#index-${index}`)
              .classList.add(CLASS_NAMES.ACTIVE);
          } else {
            // When scrolling up, show current and all previous images
            const previousImages = Array.from(
              { length: index + 1 },
              (_, i) => i
            );
            previousImages.forEach((i) => {
              document
                .querySelector(`#index-${i}`)
                .classList.add(CLASS_NAMES.ACTIVE);
            });
          }
        }
      });
    };

    this.observer = new IntersectionObserver(callback, options);
    this.steps.forEach((step) => this.observer.observe(step));

    // Store scroll handler for cleanup
    this.scrollHandler = scrollHandler;
  }

  destroy() {
    if (this.observer) {
      this.observer.disconnect();
    }
    window.removeEventListener(EVENTS.RESIZE, this.handleResize);
    window.removeEventListener(EVENTS.SCROLL, this.scrollHandler);
  }
}
